/**
 * @module ReasoningCtaButton
 * @purpose Toolbar CTA that opens/closes the reasoning panel; reflects run status visually.
 * @depends useAnalysisArtefacts, useReasoningRun, lucide-react
 * @dependedOnBy CanvasResultsArea
 *
 * @rules
 * - Performance: pure display — data fetched via hooks
 * - State: derived from hook return values; no local state needed
 * - Errors: error state shown via AlertCircle icon + tooltip
 */

import { useTranslation } from 'react-i18next'
import { Brain, Loader2, AlertCircle } from 'lucide-react'
import { useReasoningRun } from '../../../../hooks/use-reasoning'
import type { AnalysisFilters } from '../../../../types/analysis-artefacts'

const ACTIVE_STATUSES = new Set(['pending', 'running'])
const DISABLED_OPACITY = 0.45
const ENABLED_OPACITY = 1

interface ReasoningCtaButtonProps {
  caseId: string
  /**
   * Count of artefacts in the canonical canvas result set, supplied by the parent.
   *
   * This component previously ran its own `useAnalysisArtefacts` with hardcoded
   * `search: ''` / `filters: {}` — a second network round of `listArtefacts` plus facets on every
   * view, and computed from a different result set than the canvas the user was looking at, so
   * the QC gate could disagree with what was on screen.
   */
  artefactCount: number
  artefactsLoading: boolean
  selectedFilters: AnalysisFilters
  reasoningPanelOpen: boolean
  onToggle: (open: boolean) => void
}

type CtaState = 'disabled' | 'loading' | 'running' | 'error' | 'enabled'

function renderCtaIcon(ctaState: CtaState): React.ReactElement {
  if (ctaState === 'running') return <Loader2 className="h-3 w-3 animate-spin" />
  if (ctaState === 'error') return <AlertCircle className="h-3 w-3" />
  return <Brain className="h-3 w-3" />
}

/**
 * Evidence Reasoning CTA button for the canvas toolbar.
 *
 * @behaviour Disabled when no QC-gated artefacts exist and no active filters.
 *            Shows Loader2 spinner when a run is active; AlertCircle on failure.
 *            aria-pressed reflects panel open state.
 * @errors Tooltip surfaces run error message; clicking opens panel for retry.
 */
export function ReasoningCtaButton({
  caseId,
  artefactCount,
  artefactsLoading,
  selectedFilters,
  reasoningPanelOpen,
  onToggle,
}: ReasoningCtaButtonProps): React.ReactElement {
  const { t } = useTranslation('analysis')

  const { run, loading: runLoading } = useReasoningRun(caseId, true)

  const hasActiveFilters = Object.keys(selectedFilters).length > 0
  const isQcGated = artefactCount > 0 || hasActiveFilters

  function resolveCtaState(): CtaState {
    if (artefactsLoading || runLoading) return 'loading'
    if (!isQcGated) return 'disabled'
    if (run?.status !== undefined && ACTIVE_STATUSES.has(run.status)) return 'running'
    if (run?.status === 'failed') return 'error'
    return 'enabled'
  }

  const ctaState = resolveCtaState()
  const isDisabled = ctaState === 'disabled' || ctaState === 'loading'

  const tooltipMap: Record<CtaState, string> = {
    disabled: t('reasoning.cta.disabled.tooltip'),
    loading: t('reasoning.cta.disabled.tooltip'),
    running: t('reasoning.cta.running.tooltip'),
    error: t('reasoning.cta.error.tooltip'),
    enabled: t('reasoning.cta.label'),
  }

  const buttonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '6px',
    fontFamily: 'var(--font-mono)',
    fontSize: '0.7rem',
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? DISABLED_OPACITY : ENABLED_OPACITY,
    background: reasoningPanelOpen ? 'var(--accent)' : 'transparent',
    color: reasoningPanelOpen ? 'var(--btn-primary-text, #fff)' : 'var(--accent)',
    border: '1px solid var(--accent)',
    transition: 'background 0.15s, color 0.15s, opacity 0.15s',
    whiteSpace: 'nowrap',
  }

  function handleClick(): void {
    if (isDisabled) return
    onToggle(!reasoningPanelOpen)
  }

  return (
    <button
      type="button"
      style={buttonStyle}
      disabled={isDisabled}
      aria-pressed={reasoningPanelOpen}
      title={tooltipMap[ctaState]}
      onClick={handleClick}
    >
      {renderCtaIcon(ctaState)}
      {t('reasoning.cta.label')}
    </button>
  )
}
